import React, { useState, useMemo } from 'react';
import './FinanceQuotePage.scss';

// FinanceQuotePage component – renders the full finance quote UI as per mockup
export const FinanceQuotePage = () => {
    // ----- Pricing inputs -----
    const [msrp, setMsrp] = useState(38770);
    const [invoice, setInvoice] = useState(0);
    const [cost, setCost] = useState(36770);
    const [profit, setProfit] = useState(885);

    // ----- Structure -----
    const [down, setDown] = useState(1899);
    const [term, setTerm] = useState(48);

    // ----- Lender details -----
    const [apr, setApr] = useState(1.9);
    const [aprOverride, setAprOverride] = useState('');
    const [creditScore, setCreditScore] = useState('');

    // ----- Trade‑In -----
    const [acv, setAcv] = useState('');
    const [payoff, setPayoff] = useState('');
    const [tradeProfit, setTradeProfit] = useState('');

    // ----- Collapsibles -----
    const [rebatesOpen, setRebatesOpen] = useState(false);
    const [feesOpen, setFeesOpen] = useState(false);
    const [taxesOpen, setTaxesOpen] = useState(false);

    // ----- Derived calculations -----
    const sellingPrice = useMemo(() => Number(cost) + Number(profit), [cost, profit]);
    const taxRate = 9.75;
    const effectiveApr = aprOverride !== '' ? Number(aprOverride) : Number(apr);
    const basePayment = useMemo(() => {
        const principal = sellingPrice - Number(down);
        const monthlyRate = effectiveApr / 100 / 12;
        if (monthlyRate === 0) return principal / term;
        return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -term));
    }, [sellingPrice, down, term, effectiveApr]);
    const monthlyPayment = useMemo(() => basePayment * (1 + taxRate / 100), [basePayment]);

    // ----- Rebates (hard‑coded list + custom row) -----
    const [rebates, setRebates] = useState([
        { id: 0, name: '', amount: 0, applied: false, taxed: false }, // custom row – user fills name & amount
        { id: 1, name: 'HMF Special Lease', amount: 251, applied: true, taxed: true },
        { id: 2, name: '26MY Elantra‑Sonata Lease Event Cash', amount: 751, applied: true, taxed: true },
        { id: 3, name: 'Hyundai College Grad Program', amount: 401, applied: false, taxed: false },
        { id: 4, name: 'Hyundai First Responders Program', amount: 501, applied: false, taxed: false },
        { id: 5, name: 'Hyundai US Military Coupon Program', amount: 501, applied: false, taxed: false }
    ]);
    const rebateTotal = useMemo(() => rebates.reduce((sum, r) => r.applied ? sum + Number(r.amount) : sum, 0), [rebates]);

    // ----- Fees (hard‑coded list + custom row) -----
    const [fees, setFees] = useState([
        { id: 0, name: '', amount: 0, capped: false }, // custom row
        { id: 1, name: 'Tire Fee', amount: 8, capped: false },
        { id: 2, name: 'Electronic Filing Fee', amount: 35, capped: false },
        { id: 3, name: 'Doc Fee', amount: 86, capped: false },
        { id: 4, name: 'Registration Fee', amount: 360, capped: false },
        { id: 5, name: 'Acquisition Fee', amount: 651, capped: false }
    ]);
    const feesTotal = useMemo(() => fees.reduce((sum, f) => sum + Number(f.amount), 0), [fees]);

    // ----- Taxes (hard‑coded list) -----
    const [taxes] = useState([
        { id: 0, name: 'Doc Fee', amount: 86, tax: 8.29, taxed: true },
        { id: 1, name: 'Acquisition Fee', amount: 651, tax: 63.38, taxed: false },
        { id: 2, name: 'Rebate Tax', amount: 1001, tax: 97.51, taxed: true }
    ]);
    const taxesTotal = useMemo(() => taxes.reduce((sum, t) => sum + Number(t.tax), 0), [taxes]);

    // ----- Handlers for collapsibles -----
    const toggleRebates = () => setRebatesOpen(!rebatesOpen);
    const toggleFees = () => setFeesOpen(!feesOpen);
    const toggleTaxes = () => setTaxesOpen(!taxesOpen);

    // ----- Helper for number inputs (avoid NaN) -----
    const num = v => (v === '' ? '' : Number(v));

    // ----- Render -----
    return (
        <div className="finance-quote-page">
            <div className="fq-container">
                {/* Top Bar */}
                <div className="topbar">
                    <div className="topbar-title">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                        Finance Quote
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,color:'#8b95a8'}}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </div>
                    <span className="topbar-subtitle">Updated 3/12 1:07 PM</span>
                    <div className="topbar-actions">
                        <button className="btn-icon" title="Dealer Copy">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="15" y1="12" x2="15" y2="18"/><line x1="12" y1="15" x2="18" y2="15"/><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                        </button>
                        <button className="btn-icon" title="Copy">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                        </button>
                        <button className="btn-icon" title="Share">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                        </button>
                        <button className="btn-tools">
                            Tools
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                    </div>
                </div>

                {/* Main Grid */}
                <div className="main-grid">
                    {/* Column 1 – Vehicle, Location, Notes, Trade‑In */}
                    <div className="col-1-stack">
                        {/* Vehicle Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">2026 Kia Carnival</div></div>
                            <div className="fq-panel-body">
                                <div className="vehicle-trim">4D WAGON LX</div>
                                <div className="vehicle-vin">[M4222] · VIN: KNDNB5K36T6618803</div>
                            </div>
                        </div>
                        {/* Location Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header">
                                <div className="fq-panel-title">Location</div>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16,color:'#3b82f6',flexShrink:0}}><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            </div>
                            <div className="fq-panel-body">
                                <div className="location-row"><span className="location-key">Customer:</span><span className="location-val">Los Angeles County, CA, 90036</span></div>
                                <div className="location-row"><span className="location-key">Dealer:</span><span className="location-val">Los Angeles County, CA, 90036</span></div>
                            </div>
                        </div>
                        {/* Notes Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">Notes</div></div>
                            <div className="fq-panel-body" style={{flex:1, display:'flex', flexDirection:'column'}}>
                                <textarea className="description-input" placeholder="Add a note for this deal..." style={{flex:1}}></textarea>
                            </div>
                        </div>
                        {/* Trade‑In Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">Trade In</div></div>
                            <div className="fq-panel-body">
                                <div className="lender-row"><span className="lender-key">ACV</span><div className="lender-input-wrap"><span className="lender-suffix">$</span><input type="number" value={acv} onChange={e=>setAcv(e.target.value)} placeholder="0"/></div></div>
                                <div className="lender-row"><span className="lender-key">Payoff</span><div className="lender-input-wrap"><span className="lender-suffix">$</span><input type="number" value={payoff} onChange={e=>setPayoff(e.target.value)} placeholder="0"/></div></div>
                                <div className="lender-row"><span className="lender-key">Profit</span><div className="lender-input-wrap"><span className="lender-suffix">$</span><input type="number" value={tradeProfit} onChange={e=>setTradeProfit(e.target.value)} placeholder="0"/></div></div>
                            </div>
                        </div>
                    </div>

                    {/* Column 2 – Vehicle Pricing & Structure */}
                    <div className="col-2-stack">
                        {/* Vehicle Pricing Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">Vehicle Pricing</div></div>
                            <div className="fq-panel-body" style={{display:'flex', flexDirection:'column', flex:1}}>
                                <div className="pricing-row">
                                    <span className="pricing-label">MSRP</span>
                                    <div className="pricing-input-wrap">
                                        <span className="currency-prefix">$</span>
                                        <input type="number" value={msrp} onChange={e=>setMsrp(e.target.value)} />
                                    </div>
                                </div>
                                <div className="pricing-row">
                                    <span className="pricing-label">Invoice</span>
                                    <div className="pricing-input-wrap">
                                        <span className="currency-prefix">$</span>
                                        <input type="number" value={invoice} onChange={e=>setInvoice(e.target.value)} />
                                    </div>
                                </div>
                                <div className="pricing-row">
                                    <span className="pricing-label">Cost <svg className="cost-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span>
                                    <div className="pricing-input-wrap">
                                        <span className="currency-prefix">$</span>
                                        <input type="number" value={cost} onChange={e=>setCost(e.target.value)} />
                                    </div>
                                </div>
                                <div className="pricing-row">
                                    <span className="pricing-label">Profit</span>
                                    <div className="pricing-input-wrap">
                                        <span className="currency-prefix">$</span>
                                        <input type="number" value={profit} onChange={e=>setProfit(e.target.value)} />
                                    </div>
                                </div>
                                <div className="selling-price-row">
                                    <span className="selling-price-label">Selling Price</span>
                                    <div className="selling-price-value"><span className="selling-dollar">$</span>{sellingPrice.toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                        {/* Structure Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">Structure</div></div>
                            <div className="fq-panel-body">
                                <div className="pricing-row">
                                    <span className="pricing-label">Custom Down Payment</span>
                                    <div className="pricing-input-wrap">
                                        <span className="currency-prefix">$</span>
                                        <input type="number" value={down} onChange={e=>setDown(e.target.value)} />
                                    </div>
                                </div>
                                <div className="select-row">
                                    <label>Term</label>
                                    <select value={term} onChange={e=>setTerm(Number(e.target.value))}>
                                        <option value={12}>12</option>
                                        <option value={24}>24</option>
                                        <option value={36}>36</option>
                                        <option value={48}>48</option>
                                        <option value={60}>60</option>
                                        <option value={72}>72</option>
                                        <option value={84}>84</option>
                                    </select>
                                </div>
                                <div className="pricing-row" style={{justifyContent:'flex-end'}}>
                                    <button className="btn-calculate" onClick={() => {/* calculation is reactive – no action needed */}}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 01-4 4H4"/></svg>
                                        Calculate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Column 3 – Lender Details & Payment */}
                    <div className="col-3-stack">
                        {/* Lender Details Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header">
                                <div className="fq-panel-title">Lender Details</div>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16,color:'#3b82f6',flexShrink:0,cursor:'pointer'}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                            </div>
                            <div className="fq-panel-body">
                                <div className="lender-name">Kia Motors Finance <span className="info-icon">i</span></div>
                                <div className="lender-row">
                                    <span className="lender-key">Applicant Credit Score</span>
                                    <div className="lender-input-wrap"><input type="number" placeholder="780" value={creditScore} onChange={e=>setCreditScore(e.target.value)} /></div>
                                </div>
                                <div className="lender-row">
                                    <span className="lender-key">APR</span>
                                    <div className="lender-input-wrap"><input type="number" value={apr} onChange={e=>setApr(e.target.value)} step="0.1"/> <span className="lender-suffix">%</span></div>
                                </div>
                                <div className="lender-row">
                                    <span className="lender-key">APR Override</span>
                                    <div className="lender-input-wrap"><input type="number" placeholder="—" value={aprOverride} onChange={e=>setAprOverride(e.target.value)} step="0.1"/> <span className="lender-suffix">%</span></div>
                                </div>
                            </div>
                        </div>
                        {/* Payment Card */}
                        <div className="fq-panel">
                            <div className="fq-panel-header"><div className="fq-panel-title">Payment</div></div>
                            <div className="fq-panel-body">
                                <div className="lender-row"><span className="lender-key">Base Payment</span><div className="lender-val-plain"><span className="lender-suffix">$</span>{basePayment.toFixed(2)}</div></div>
                                <div className="lender-row"><span className="lender-key">Tax Rate</span><div className="lender-val-plain" style={{justifyContent:'flex-end'}}>{taxRate}<span className="lender-suffix"> %</span></div></div>
                                <div className="lender-row" style={{borderTop:'1px solid #252d3d'}}>
                                    <span className="lender-key" style={{fontWeight:700,color:'#f0f2f5'}}>Monthly Payment</span>
                                    <div className="monthly-val-wrap"><span className="dollar">$</span><span className="amount">{monthlyPayment.toFixed(2)}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Wrap – Rebates, Fees, Taxes */}
                <div className="bottom-wrap">
                    {/* Rebates Panel */}
                    <div className="fq-panel panel-rebates">
                        <div className="fq-panel-header collapsible" onClick={toggleRebates}>
                            <div className="panel-title" style={{flex:1}}>Rebates</div>
                            <div className="sc-check"></div>
                            <div className="sc-check"></div>
                            <div className="sc-amt"><span style={{fontSize:13,fontWeight:600,color:'#e0e0e0'}}>$<span>{rebateTotal.toLocaleString()}</span></span></div>
                            <svg className="chevron-icon" style={{transform: rebatesOpen ? 'rotate(0deg)' : 'rotate(180deg)'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 15 12 9 17 15"/></svg>
                        </div>
                        {rebatesOpen && (
                        <div className="panel-body">
                            <div className="col-header">
                                <span style={{flex:1}}>Name</span>
                                <span className="sc-check" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Applied</span>
                                <span className="sc-check" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Taxed</span>
                                <span className="sc-amt" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Amount</span>
                            </div>
                            {rebates.map((r, i) => (
                                <div className="sec-row" key={r.id}>
                                    <div className="sc-name" style={{overflow:'visible'}}>
                                        <input type="text" placeholder={i===0 ? 'Custom Rebate Name' : ''} value={r.name} onChange={e=>{
                                            const newRebates = [...rebates];
                                            newRebates[i].name = e.target.value;
                                            setRebates(newRebates);
                                        }}/>
                                        {i===0 && (<button className="sc-add-btn" style={{marginLeft:6}} onClick={()=>{
                                            const newRebates = [...rebates];
                                            newRebates.push({id: Date.now(), name:'', amount:0, applied:false, taxed:false});
                                            setRebates(newRebates);
                                        }}>+ Add</button>)
                                        }
                                    </div>
                                    <div className="sc-check"><input type="checkbox" className="rebate-cb" checked={r.applied} onChange={e=>{
                                        const newRebates = [...rebates];
                                        newRebates[i].applied = e.target.checked;
                                        setRebates(newRebates);
                                    }}/></div>
                                    <div className="sc-check"><input type="checkbox" className="rebate-cb" checked={r.taxed} onChange={e=>{
                                        const newRebates = [...rebates];
                                        newRebates[i].taxed = e.target.checked;
                                        setRebates(newRebates);
                                    }}/></div>
                                    <div className="sc-amt"><div className="sc-input"><span className="pfx">$</span><input type="number" value={r.amount} onChange={e=>{
                                        const newRebates = [...rebates];
                                        newRebates[i].amount = e.target.value;
                                        setRebates(newRebates);
                                    }}/></div></div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                    {/* Fees Panel */}
                    <div className="fq-panel">
                        <div className="fq-panel-header collapsible" onClick={toggleFees}>
                            <div className="panel-title" style={{flex:1}}>Fees</div>
                            <div className="sc-capped"></div>
                            <div className="sc-amt"><span style={{fontSize:13,fontWeight:600,color:'#e0e0e0'}} id="fees-total">${feesTotal.toLocaleString()}</span></div>
                            <svg className="chevron-icon" style={{transform: feesOpen ? 'rotate(0deg)' : 'rotate(180deg)'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 15 12 9 17 15"/></svg>
                        </div>
                        {feesOpen && (
                        <div className="panel-body">
                            <div className="col-header">
                                <span style={{flex:1}}>Name</span>
                                <span className="sc-capped" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                                    <label className="mswitch" title="Cap all / All upfront"><input type="checkbox"/><span className="mswitch-track"></span><span className="mswitch-thumb"></span></label>
                                </span>
                                <span className="sc-amt" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Amount</span>
                            </div>
                            {fees.map((f,i)=> (
                                <div className="sec-row" key={f.id}>
                                    <div className="sc-name" style={{overflow:'visible'}}>
                                        <input type="text" placeholder={i===0?'Custom Fee Name':''} value={f.name} onChange={e=>{
                                            const newFees=[...fees];
                                            newFees[i].name=e.target.value; setFees(newFees);
                                        }}/>
                                        {i===0 && (<button className="sc-add-btn" style={{marginLeft:6}} onClick={()=>{
                                            const newFees=[...fees];
                                            newFees.push({id:Date.now(),name:'',amount:0,capped:false});
                                            setFees(newFees);
                                        }}>+ Add</button>) }
                                    </div>
                                    <div className="sc-capped"><label className="mswitch"><input type="checkbox" checked={f.capped} onChange={e=>{const newFees=[...fees];newFees[i].capped=e.target.checked;setFees(newFees);}}/><span className="mswitch-track"></span><span className="mswitch-thumb"></span></label></div>
                                    <div className="sc-amt"><div className="sc-input"><span className="pfx">$</span><input type="number" value={f.amount} onChange={e=>{const newFees=[...fees];newFees[i].amount=e.target.value;setFees(newFees);}}/></div></div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                    {/* Taxes Panel */}
                    <div className="fq-panel">
                        <div className="fq-panel-header collapsible" onClick={toggleTaxes}>
                            <div className="panel-title" style={{flex:1,display:'flex',gap:8}}>
                                Taxes
                                <input type="checkbox" checked={true} style={{width:14,height:14,cursor:'pointer',accentColor:'#3b82f6'}} onClick={e=>e.stopPropagation()} />
                            </div>
                            <div className="sc-amt"></div>
                            <div className="sc-capped"></div>
                            <div className="sc-tax"><span style={{fontSize:13,fontWeight:600,color:'#e0e0e0'}} id="taxes-total">${taxesTotal.toLocaleString()}</span></div>
                            <svg className="chevron-icon" style={{transform: taxesOpen ? 'rotate(0deg)' : 'rotate(180deg)'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 15 12 9 17 15"/></svg>
                        </div>
                        {taxesOpen && (
                        <div className="panel-body">
                            <div className="col-header">
                                <span style={{flex:1}}>Name</span>
                                <span className="sc-amt" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Taxable Amt</span>
                                <span className="sc-capped" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                                    <label className="mswitch" style={{opacity:0.35,cursor:'default'}}><input type="checkbox" disabled/><span className="mswitch-track"></span><span className="mswitch-thumb"></span></label>
                                </span>
                                <span className="sc-tax" style={{fontSize:11,color:'#8b95a8',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>Tax</span>
                            </div>
                            {taxes.map(t=> (
                                <div className="sec-row" key={t.id}>
                                    <div className="sc-name">{t.name}</div>
                                    <div className="sc-amt"><span className="sc-val">${t.amount}</span></div>
                                    <div className="sc-capped"><label className="mswitch"><input type="checkbox" checked={t.taxed} onChange={e=>{/* no effect in mock */}}/><span className="mswitch-track"></span><span className="mswitch-thumb"></span></label></div>
                                    <div className="sc-tax">${t.tax}</div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceQuotePage;
